import { useState } from "react";
import { QrCode, CreditCard, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useRestaurant } from "../hooks/useRestaurant";
import { EditRestaurantForm } from "./EditRestaurantForm";
import { QRCodeModal } from "./QRCodeModal";
import { subscriptionsApi } from "@/api/subscriptions";

const PUBLIC_MENU_BASE_URL =
  import.meta.env.VITE_PUBLIC_MENU_BASE_URL || "https://swiftmenu.com.br";

function buildMenuUrl(restaurantId: number) {
  return `${PUBLIC_MENU_BASE_URL.replace(/\/$/, "")}/pt/menu/${restaurantId}`;
}

export function SettingsView() {
  const { data: restaurant, isLoading, error } = useRestaurant();
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    setPortalError(null);
    try {
      const { url } = await subscriptionsApi.getCustomerPortalUrl();
      window.location.href = url;
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number } };
      if (axiosErr.response?.status === 404) {
        setPortalError("No active subscription found.");
      } else {
        setPortalError("Failed to open subscription portal. Please try again.");
      }
      setPortalLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          Failed to load restaurant settings. Please try again later.
        </AlertDescription>
      </Alert>
    );
  }

  const menuUrl = restaurant ? buildMenuUrl(restaurant.id) : null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Settings</h1>
          <p className="text-muted-foreground">
            Manage your restaurant profile
          </p>
        </div>
      </div>

      {/* QR Code Card */}
      <Card>
        <CardHeader>
          <CardTitle>QR Code Menu</CardTitle>
          <CardDescription>
            Generate a QR code for customers to access your menu
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <p className="text-sm font-medium">Menu URL</p>
              {menuUrl ? (
                <a
                  href={menuUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary hover:underline"
                >
                  {menuUrl}
                </a>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Save your restaurant profile to generate your menu URL
                </p>
              )}
            </div>
            <Button
              variant="outline"
              onClick={() => setQrModalOpen(true)}
              disabled={!menuUrl}
            >
              <QrCode className="mr-2 h-4 w-4" />
              View QR Code
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Subscription Card */}
      <Card>
        <CardHeader>
          <CardTitle>Subscription</CardTitle>
          <CardDescription>
            Manage your billing and subscription plan
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {portalError && (
            <Alert variant="destructive">
              <AlertDescription>{portalError}</AlertDescription>
            </Alert>
          )}
          <Button
            variant="outline"
            onClick={handleManageSubscription}
            disabled={portalLoading}
          >
            {portalLoading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <CreditCard className="mr-2 h-4 w-4" />
            )}
            Manage Subscription
          </Button>
        </CardContent>
      </Card>

      {/* Restaurant Details */}
      {restaurant && <EditRestaurantForm restaurant={restaurant} />}

      {/* QR Code Modal */}
      {menuUrl && (
        <QRCodeModal
          open={qrModalOpen}
          onClose={() => setQrModalOpen(false)}
          menuUrl={menuUrl}
          restaurantName={restaurant?.name || "Restaurant"}
        />
      )}
    </div>
  );
}
